import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Switch,
  Select,
  Typography,
  Alert,
  Spin,
  Descriptions,
  Row,
  Col,
  App,
} from 'antd';
import { useAdvanceEligibility } from '../../hooks/useSalesOrder';
import { useCreateAdvanceInvoice, useChartAccountsByType } from '../../hooks/useSalesInvoice';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE, uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Text } = Typography;

interface CreateAdvanceInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (invoiceId: string) => void;
  salesOrderId: string;
}

export const CreateAdvanceInvoiceModal: React.FC<CreateAdvanceInvoiceModalProps> = ({
  open,
  onClose,
  onSuccess,
  salesOrderId,
}) => {
  const { message } = App.useApp();
  const { baseCurrency } = useCurrency();
  const [form] = Form.useForm();
  const [applyTax, setApplyTax] = useState(false);

  const { data: eligibility, isLoading: loadingEligibility } = useAdvanceEligibility(open ? salesOrderId : undefined);
  const { data: liabilityAccounts = [], isLoading: loadingLiability } = useChartAccountsByType('Liability', false);
  const { data: assetAccounts = [], isLoading: loadingAssets } = useChartAccountsByType('Asset', false);
  const loadingAccounts = loadingLiability || loadingAssets;
  const createMutation = useCreateAdvanceInvoice();

  const currencySymbol =
    uiPlainCurrencyLabel(baseCurrency?.symbol, baseCurrency?.currency_code || baseCurrency?.code) ||
    AED_CURRENCY_CODE;
  const remainingAdvance = eligibility?.remainingAdvance ?? 0;
  const advancePaymentAmount = eligibility?.advancePaymentAmount ?? 0;
  const advanceInvoicedAmount = eligibility?.advanceInvoicedAmount ?? 0;
  const salesOrder = eligibility?.sales_order;

  useEffect(() => {
    if (salesOrder?.department_id) {
      form.setFieldsValue({ department_id: salesOrder.department_id });
    }
  }, [salesOrder?.department_id, form]);

  const amount = Form.useWatch('amount', form) as number | undefined;
  const vatPct = Form.useWatch('vat_percentage', form) as number | undefined;
  const baseAmount = useMemo(() => {
    const a = amount != null && amount > 0 ? Number(amount) : 0;
    return Math.min(a, remainingAdvance);
  }, [amount, remainingAdvance]);
  const vatAmount = useMemo(() => {
    if (!applyTax || baseAmount <= 0) return 0;
    const pct = vatPct != null ? Number(vatPct) : 5;
    return (baseAmount * pct) / 100;
  }, [applyTax, baseAmount, vatPct]);
  const grossTotal = baseAmount + vatAmount;

  const handleClose = () => {
    form.resetFields();
    setApplyTax(false);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        amount: Number(values.amount),
        apply_tax: !!values.apply_tax,
        vat_percentage: values.apply_tax ? Number(values.vat_percentage ?? 5) : undefined,
        vat_account_id: values.apply_tax ? values.vat_account_id : undefined,
        prepaid_customer_advance_account_id: values.prepaid_customer_advance_account_id,
        advance_from_customer_account_id: values.advance_from_customer_account_id,
        department_id: values.department_id,
      };
      const invoice = await createMutation.mutateAsync({ salesOrderId, payload });
      handleClose();
      onSuccess(invoice.id);
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e && (e as { errorFields?: unknown }).errorFields)
        return;
      const apiMessage = (e as any)?.response?.data?.message;
      message.error(apiMessage ?? (e as Error)?.message ?? 'Failed to create Proforma Invoice');
    }
  };

  const notEligible = open && !loadingEligibility && !eligibility?.eligible;
  const noRemaining = open && !loadingEligibility && eligibility?.eligible && remainingAdvance <= 0;

  return (
    <Modal
      title="Create Proforma Invoice (Advance)"
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Create Proforma Invoice"
      confirmLoading={createMutation.isPending}
      width={640}
      destroyOnHidden
      okButtonProps={{ disabled: notEligible || noRemaining || loadingEligibility }}
    >
      {loadingEligibility && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}
      {notEligible && !loadingEligibility && (
        <Alert
          type="warning"
          message="No advance defined"
          description="This Customer Order has no advance % or advance amount. Proforma Invoice is only available when advance is defined."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {noRemaining && !loadingEligibility && (
        <Alert
          type="info"
          message="No remaining advance"
          description="The advance amount for this order has already been fully invoiced."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {!loadingEligibility && eligibility?.eligible && remainingAdvance > 0 && (
        <>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Customer Order">
              {salesOrder?.sales_order_number ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">{salesOrder?.customer_name ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="SO Grand Total">
              {currencySymbol} {Number(salesOrder?.grand_total ?? 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Advance %">
              {Number(salesOrder?.advance_payment_percentage ?? 0).toFixed(2)}%
            </Descriptions.Item>
            <Descriptions.Item label="Advance amount">
              {currencySymbol} {advancePaymentAmount.toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Already advance-invoiced">
              {currencySymbol} {advanceInvoicedAmount.toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Remaining advance">
              <Text strong>{currencySymbol} {remainingAdvance.toFixed(2)}</Text>
            </Descriptions.Item>
          </Descriptions>

          {!loadingAccounts && liabilityAccounts.length === 0 && (
            <Alert
              type="warning"
              message="No liability accounts found"
              description="You need liability accounts for Prepaid Customer Advance (credit) and VAT (if tax applies), e.g. 2210 Prepaid Customer Advance. Add them under Finance > Chart of Accounts."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {!loadingAccounts && assetAccounts.length === 0 && (
            <Alert
              type="warning"
              message="No asset accounts found"
              description="The debit side (e.g. Accounts Receivable) uses asset accounts. Add AR under Finance > Chart of Accounts (typically account type Asset)."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              amount: remainingAdvance,
              apply_tax: false,
              vat_percentage: 5,
            }}
          >
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
            >
              <DepartmentSelect />
            </Form.Item>
            <Form.Item
              name="amount"
              label="Advance amount to invoice"
              rules={[
                { required: true, message: 'Amount is required' },
                { type: 'number', min: 0.01, message: 'Amount must be greater than zero' },
                {
                  type: 'number',
                  max: remainingAdvance,
                  message: `Amount cannot exceed remaining advance (${remainingAdvance.toFixed(2)})`,
                },
              ]}
            >
              <InputNumber
                min={0.01}
                max={remainingAdvance}
                step={0.01}
                style={{ width: '100%' }}
                addonAfter={currencySymbol}
              />
            </Form.Item>

            <Form.Item name="apply_tax" label="Apply tax on advance" valuePropName="checked">
              <Switch onChange={(checked) => setApplyTax(checked)} />
            </Form.Item>

            {applyTax && (
              <>
                <Form.Item
                  name="vat_percentage"
                  label="VAT %"
                  rules={[{ required: true, message: 'VAT % is required when tax is applied' }]}
                >
                  <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  name="vat_account_id"
                  label="VAT / Tax account"
                  rules={[{ required: true, message: 'VAT account is required when tax is applied' }]}
                >
                  <Select
                    placeholder="Select VAT account"
                    loading={loadingAccounts}
                    showSearch
                    optionFilterProp="label"
                    options={liabilityAccounts.map((a) => ({
                      value: a.id,
                      label: `${a.account_code} - ${a.account_name}`,
                    }))}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="prepaid_customer_advance_account_id"
              label="Prepaid Customer Advance account (Credit)"
              rules={[{ required: true, message: 'Prepaid Customer Advance account is required' }]}
            >
              <Select
                placeholder="Select account"
                loading={loadingAccounts}
                showSearch
                optionFilterProp="label"
                options={liabilityAccounts.map((a) => ({
                  value: a.id,
                  label: `${a.account_code} - ${a.account_name}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="advance_from_customer_account_id"
              label="Advance from Customer account (Debit)"
              extra="Typically Accounts Receivable (asset). Liability accounts are used for Prepaid Customer Advance (credit), not for this debit."
              rules={[{ required: true, message: 'Advance from Customer account is required' }]}
            >
              <Select
                placeholder="Select account (e.g. Accounts Receivable)"
                loading={loadingAccounts}
                showSearch
                optionFilterProp="label"
                options={assetAccounts.map((a) => ({
                  value: a.id,
                  label: `${a.account_code} - ${a.account_name}`,
                }))}
              />
            </Form.Item>
          </Form>

          <Row gutter={16} style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
            <Col span={8}>
              <Text type="secondary">Base amount</Text>
              <div>
                <Text strong>{currencySymbol} {baseAmount.toFixed(2)}</Text>
              </div>
            </Col>
            <Col span={8}>
              <Text type="secondary">Tax amount</Text>
              <div>
                <Text strong>{currencySymbol} {vatAmount.toFixed(2)}</Text>
              </div>
            </Col>
            <Col span={8}>
              <Text type="secondary">Gross total</Text>
              <div>
                <Text strong>{currencySymbol} {grossTotal.toFixed(2)}</Text>
              </div>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
};
